<template>
    <v-card>
        <v-card-title>
            {{$t('event_menu.features')}}
            <v-spacer></v-spacer>
            <v-btn v-if="isEditOrderMode" color="red" dark class="ml-3" @click="cancelEditOrder">
                {{$t('Cancel')}}
                <v-icon right>mdi-close-circle-outline</v-icon>
            </v-btn>
            <v-btn v-if="isEditOrderMode" color="green" dark class="ml-3" @click="saveEditOrder">
                {{$t('Save')}}
                <v-icon right>mdi-check</v-icon>
            </v-btn>
            <v-btn v-if="!isEditOrderMode" color="primary" @click="initEditOrder">
                {{$t('Reorder')}}
                <v-icon right>mdi-swap-vertical</v-icon>
            </v-btn>
            <div class="button-spacer"></div>
            <v-btn  v-if="!isEditOrderMode" color="primary" @click="initAddFeature">
                {{$t('add')}}
                <v-icon right>mdi-plus</v-icon>
            </v-btn>
        </v-card-title>
        <v-data-table
            id="menuFeatureList"
            :headers="headers"
            :items="tableMenuFeatures"
            :loading="isMenuFeaturePending"
            @pagination="currentPagination"
            disable-sort>
            <template v-slot:item.scroller="{value}">
                <div v-show="isEditOrderMode" class="handle"><v-btn icon ><v-icon>drag_handle{{value}}</v-icon></v-btn></div>
                <div v-show="!isEditOrderMode"></div>
            </template>
            <template v-slot:item.createdAt="{value}">
                {{DateTime.fromISO(value).toFormat('yyyy-MM-dd HH:mm')}}
            </template>
            <template v-slot:item.updatedAt="{value}">
                {{DateTime.fromISO(value).toFormat('yyyy-MM-dd HH:mm')}}
            </template>
            <template v-slot:item.icon="{value}">
                <v-img :src="value.src" max-width="5rem" max-height="5rem"></v-img>
            </template>
            <template v-slot:item.feature="{value}">
                {{value ? value.name : ''}}
            </template>
            <template v-slot:item.actions="{item}">
                <v-btn color="primary" class="ma-2"
                       @click="initEditFeature(item)">
                    {{$t('edit')}}
                    <v-icon right>mdi-pencil</v-icon>
                </v-btn>
                <v-btn color="red" dark class="ma-2"
                       @click="deleteFeature(item)">
                    {{$t('delete')}}
                    <v-icon right>mdi-delete</v-icon>
                </v-btn>
            </template>
        </v-data-table>

        <v-dialog v-model="editFeatureDialogShown" max-width="600px" persistent="">
            <v-card>
                <v-card-title>
                    {{$t('menu_feature.title')}}
                </v-card-title>
                <v-card-text>
                    <v-container>
                        <v-form v-model="isEditFeatureFormValid">

                            <v-row>
                                <v-col>
                                    <v-text-field outlined
                                                  :label="$t('menu_feature.order')"
                                                  :placeholder="$t('menu_feature.order')"
                                                   :rules="[featureOrderRule]"
                                                  v-model="editingFeature.order"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col>
                                    <v-text-field outlined
                                                  :label="$t('menu_feature.name')"
                                                  :placeholder="$t('menu_feature.name')"    
                                                  v-model="editingFeature.name"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col>
                                    <v-select outlined
                                              clearable
                                              :label="$t('menu_feature.feature')"
                                              :placeholder="$t('menu_feature.feature')"
                                              :items="featureOptionsFiltered"
                                              @change="onChangeFeatureOptions"
                                              v-model="editingFeature.featureId"></v-select>
                                </v-col>
                            </v-row>
                            <v-row align="center">
                                <v-col>
                                    <v-file-input accept="image/*"
                                                :label="$t('menu_feature.icon') + ' ' + $t('menu_feature.recommendedIconSize')"
                                                :disabled="isDisabled"
                                                :clearable="!isDisabled"
                                                v-model="editingFeature.icon">
                                        <template v-slot:selection="{file}">
                                            <v-img :src="getFileUrl(file)"
                                                max-width="250px"
                                                max-height="250px"></v-img>
                                        </template>
                                    </v-file-input>
                                </v-col>
                                <v-col align="center">
                                    <v-radio-group v-model="useDefaultIcon" :mandatory="true" @change="onChangeRadioSelection">
                                        <v-radio :label="$t('menu_feature.default_icon')" value="default"></v-radio>
                                        <v-radio :label="$t('menu_feature.personal_icon')" value="personal"></v-radio>
                                    </v-radio-group>
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-container>
                </v-card-text>
                <v-card-actions>
                    <v-btn color="secondary" @click="confirmCancelItemDialogShown = true">{{$t('cancel')}}</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" @click="saveFeature"
                           :disabled="!isEditFeatureFormValid">{{$t('save')}}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <PopupAlert
            v-model="confirmCancelItemDialogShown"
            title="Warning"
            message="Do you wish to cancel your amendments?"
            :onYes="cancelEditFeatureItem"
            :onNo="function() {confirmCancelItemDialogShown=false}"
            ></PopupAlert>
        <v-overlay :z-index="999" v-model="overlay">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
    </v-card>
</template>
<style scoped>
    .button-spacer {
        width:10px;
    }
</style>
<script>
    import {computed, ref, onMounted,watch} from "@vue/composition-api";
    import {useFind} from "feathers-vuex";
    import {DateTime} from "luxon";
    import * as lodash from "lodash"
    import Sortable from "sortablejs";
    import PopupAlert from "../../components/PopupAlert";

    export default {
        name: 'EventMenuFeatureList',
        props: {
            homeId: String,
        },
        components:{PopupAlert},
        setup(props, {root}) {
            const {MenuFeature, Feature, EventFeature} = root.$FeathersVuex.api;
            const eventId = computed(() => root.$route.params.eventId);

            // Features
            const menuFeatureParams = computed(() => {
                return {
                    query: {
                        fHomeId: props.homeId,
                        $sort: {order: 1},
                    }
                }
            });
            const menuFeatureQueryWhen = computed(() => {
                return !!props.homeId;
            });
            const {items: menuFeatures, isPending: isMenuFeaturePending} = useFind({
                model: MenuFeature,
                params: menuFeatureParams,
                queryWhen: menuFeatureQueryWhen,
            });
            // Data Table
            const headers = ref([
                {text: '', value:'scroller',width:80},
                {text: root.$t('menu_feature.order'), value: 'order'},
                {text: root.$t('menu_feature.icon'), value: 'icon'},
                {text: root.$t('menu_feature.name'), value: 'name'},
                {text: root.$t('menu_feature.feature'), value: 'feature'},
                {text: '', value: 'actions'},
            ]);
            
            watch(isMenuFeaturePending,(newval,oldval)=>{
                if(!newval && oldval){
                    editOrderMenuFeatures.value = menuFeatures.value
                }
            })
            // Add / Edit Feature
            const editFeatureDialogShown = ref(false);
            const confirmCancelItemDialogShown = ref(false);
            const isEditFeatureFormValid = ref(false);
            const editingFeature = ref(new MenuFeature({fHomeId: props.homeId}));
            const isNewFeature = ref(false);
            const useDefaultIcon = ref("default");
            const tempPersonalIcon = ref(null);
            const isDisabled = ref(false);

            const currentStart = ref(0);

            const currentPagination = function(e){
                currentStart.value = e.pageStart
            }
            const initAddFeature = function () {
                useDefaultIcon.value = "default"
                isDisabled.value = true
                isNewFeature.value = true;
                tempPersonalIcon.value = null // reset temp personal icon

                editingFeature.value = new MenuFeature({fHomeId: props.homeId});
                editFeatureDialogShown.value = true;
                editingFeature.value.order = menuFeatures.value.length + 1
                editingFeature.value.isDefaultIcon = true;
            };
            const initEditFeature = function (feature) {
                editingFeature.value = lodash.cloneDeep(feature);
                editFeatureDialogShown.value = true;
                // Check if the editing feature is currently using default icon or not
                const tempfeature = getFeatureOptionById(editingFeature.value.featureId)
                if(tempfeature.img.src === editingFeature.value.icon.src){
                    useDefaultIcon.value = "default"
                    isDisabled.value = true
                    editingFeature.value.isDefaultIcon = true
                }else{
                    useDefaultIcon.value = "personal"
                    editingFeature.value.isDefaultIcon = false
                    isDisabled.value = false
                }
                isNewFeature.value = false
            };
            const deleteFeature = function (feature) {
                if (confirm(root.$t('menu_feature.confirm_delete'))) {
                    feature.remove().then(() => {
                    }, root.$handleFeatherError.bind(root));
                }
            };

            // EventFeature
            const eventFeatureParams = computed(() => {
                return {
                    query: {
                        eventId: eventId.value,
                    }
                }
            });
            const {items: eventFeatures, isPending: isEventFeaturePending} = useFind({
                model: EventFeature,
                params: eventFeatureParams,
            });
            const existingEventFeatureIds = computed(() => eventFeatures.value.map(ef => ef.featureId));

            const featureParams = computed(() => {
                return {
                    query: {
                        id: {
                            $in: existingEventFeatureIds.value,
                        }
                    }
                }
            });
            const {items: features, isPending: isFeaturePending} = useFind({model: Feature, params: featureParams});
            const featureOptions = computed(() => {
                return features.value.map(f => {
                    return {text: f.name, value: f.id}
                });
            });

            const featureOptionsFiltered = computed(()=>{
                let listFiltered = []
                let listContained = []
                if(menuFeatures.value && menuFeatures.value.length>0){
                    listContained = menuFeatures.value.map(f =>{
                        if(f.feature==null){
                            return null
                        }
                        return f.feature.name
                    });
                }
                for(let i=0;i<features.value.length;i++){
                    if(isNewFeature.value){
                        if(listContained.includes(features.value[i].name)){
                            continue
                        }else{
                            listFiltered.push({text:features.value[i].name,value:features.value[i].id,img: features.value[i].default_icon})
                        }
                    }else{
                        if(listContained.includes(features.value[i].name) && editingFeature.value.feature!=null && features.value[i].name !== editingFeature.value.feature.name){
                            continue 
                        }else{
                            listFiltered.push({text:features.value[i].name,value:features.value[i].id, img: features.value[i].default_icon})
                        }
                    }
                }
                return listFiltered
            })

            const getFeatureOptionById = function(id){
                for(let i=0;i<featureOptionsFiltered.value.length;i++){
                    if(featureOptionsFiltered.value[i].value===id){
                        return featureOptionsFiltered.value[i]
                    }
                }
                return null
            }
            const saveFeature = async function () {
                try {
                    if (editingFeature.value.icon instanceof File) {
                        editingFeature.value.icon = {src: await root.$uploadFile(editingFeature.value.icon)};
                    }
                    if (!editingFeature.value.icon) {
                        editingFeature.value.icon = null;
                    }
                    editingFeature.value.save().then(() => {
                        MenuFeature.get(editingFeature.value.id);
                        editFeatureDialogShown.value = false;
                        root.$store.dispatch('showSnackBar', root.$t('menu_feature.save_successful'));
                    }, root.$handleFeatherError.bind(root));
                    tempPersonalIcon.value = null
                } catch (e) {
                    root.$handleFeatherError(e);
                }
            };

            const cancelEditFeatureItem = function(){
                editFeatureDialogShown.value = false;
                confirmCancelItemDialogShown.value = false;
                isNewFeature.value = false;
                tempPersonalIcon.value = null
            }

            const featureOrderRule = function(value){
                const onlyNumberRule =  /^[0-9]+$/;
                return onlyNumberRule.test(value) || 'Invalid Number';
            }

            const featureOrderRule2 = function(value){
                let length = menuFeatures.value.length;

                if(isNewFeature.value){
                    return ((value==length+1) && value>=0) || 'Number out of range';
                }else{
                    return (value>=0 && (value<=length)) || 'Number out of range';
                }
            }
            const onChangeFeatureOptions = function(value){
                // if(useDefaultIcon.value==="personal")return
                editingFeature.value.icon = getFeatureOptionById(value).img
                useDefaultIcon.value = "default";
                editingFeature.value.isDefaultIcon = true
            }
            const onChangeRadioSelection = function(value){
                if(value==="default"){
                    isDisabled.value = true
                    useDefaultIcon.value = "default";
                    tempPersonalIcon.value = editingFeature.value.icon
                    editingFeature.value.icon = getFeatureOptionById(editingFeature.value.featureId).img
                    editingFeature.value.isDefaultIcon = true
                }else{
                    isDisabled.value = false
                    useDefaultIcon.value = "personal";
                    editingFeature.value.isDefaultIcon = false
                    if(tempPersonalIcon.value!=null){
                        editingFeature.value.icon = tempPersonalIcon.value
                    }else{
                        editingFeature.value.icon = null
                    }
                }
            }

            const editOrderMenuFeatures = ref([]);
            const isEditOrderMode = ref(false);
            const didOrderChange = ref(false);
            const overlay = ref(false);


            const initEditOrder = function(){
                didOrderChange.value = false
                isEditOrderMode.value = true
                editOrderMenuFeatures.value = lodash.cloneDeep(menuFeatures.value)
            }

            const cancelEditOrder = function(){
                isEditOrderMode.value = false;
            }

            const saveEditOrder = async function(){
                if(!didOrderChange.value){
                    isEditOrderMode.value = false
                    return;
                }

                overlay.value = true
                for(let i=0;i<editOrderMenuFeatures.value.length;i++){
                    await editOrderMenuFeatures.value[i].save().then(()=>{
                        MenuFeature.get(editOrderMenuFeatures.value[i].id)
                    })
                }
                isEditOrderMode.value = false
                overlay.value = false;
            }
            onMounted(()=>{
                let table = document.querySelector('#menuFeatureList tbody');
                Sortable.create(table,
                    {
                        handle:".handle", // Look for class with name "handle"
                        onEnd({newIndex,oldIndex}){
                            newIndex +=  currentStart.value;
                            oldIndex +=  currentStart.value;
                            const rowSelected = editOrderMenuFeatures.value.splice(oldIndex,1)[0];
                            editOrderMenuFeatures.value.splice(newIndex,0,rowSelected);

                            didOrderChange.value = true
                            for(let i=0;i<editOrderMenuFeatures.value.length;i++){
                                editOrderMenuFeatures.value[i].order = i+1
                            }
                        }
                    }
                )
            })

            const tableMenuFeatures = computed(()=>{
                return isEditOrderMode.value?editOrderMenuFeatures.value:menuFeatures.value;
            })
            return {
                menuFeatures,
                tableMenuFeatures,
                isMenuFeaturePending,

                headers,

                editFeatureDialogShown,
                confirmCancelItemDialogShown,
                initAddFeature,
                initEditFeature,
                deleteFeature,
                isEditFeatureFormValid,
                editingFeature,
                featureOptions,
                onChangeFeatureOptions,
                saveFeature,
                cancelEditFeatureItem,

                featureOrderRule,
                featureOrderRule2,

                featureOptionsFiltered,
                isNewFeature,
                DateTime,
                getFileUrl: root.$getFileURL,

                useDefaultIcon,
                onChangeRadioSelection,
                isDisabled,

                currentPagination,

                initEditOrder,
                saveEditOrder,
                cancelEditOrder,
                isEditOrderMode,
                overlay
            }
        }
    }
</script>
